/**
 * Bundle Builder
 * Preview prices only. Shopify Automatic Discounts determine cart/checkout totals.
 */
if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.selections = new Map();
        this.bundleSize = 2;
        this.isSubmitting = false;
        this.toastTimer = null;
        this.onCardClick = this.onCardClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onTrayClick = this.onTrayClick.bind(this);
        this.onPackClick = this.onPackClick.bind(this);
        this.onCtaClick = this.onCtaClick.bind(this);
        this.onDrawerClosed = this.onDrawerClosed.bind(this);
      }

      connectedCallback() {
        document.body.classList.add('bundle-builder-page');
        window.wetheme?.webcomponentRegistry?.register({
          key: 'component-bundle-builder',
        });
        this.cacheElements();
        this.readConfig();
        this.bindEvents();
        this.setBundleSize(this.bundleSize, { silent: true });
        this.render();
      }

      disconnectedCallback() {
        this.cards.forEach((card) => {
          card.removeEventListener('click', this.onCardClick);
          card
            .querySelector('[data-bundle-variant]')
            ?.removeEventListener('change', this.onVariantChange);
        });
        this.packButtons.forEach((el) => {
          el.removeEventListener('click', this.onPackClick);
        });
        this.tray?.removeEventListener('click', this.onTrayClick);
        this.cta?.removeEventListener('click', this.onCtaClick);
        window.eventBus?.off('drawer:closed', this.onDrawerClosed);
        this.gokwikObserver?.disconnect();
        if (this.checkoutTimer) clearTimeout(this.checkoutTimer);
        if (this.toastTimer) clearTimeout(this.toastTimer);
        if (this.celebrateTimer) clearTimeout(this.celebrateTimer);
        document.querySelector('[data-bundle-celebration-root]')?.remove();
        document.body.classList.remove(
          'bundle-builder-page',
          'bundle-celebrate',
          'bundle-drawer-open'
        );
      }

      onDrawerClosed() {
        document.body.classList.remove('bundle-drawer-open');
        this.gokwikObserver?.disconnect();
        if (this.checkoutTimer) clearTimeout(this.checkoutTimer);
      }

      cacheElements() {
        this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
        this.packButtons = Array.from(this.querySelectorAll('[data-bundle-pack]'));
        this.steps = Array.from(this.querySelectorAll('[data-bundle-steps] [data-step]'));
        this.introEl = this.querySelector('[data-bundle-intro]');
        this.maxLabelEl = this.querySelector('[data-bundle-max]');
        this.counts = this.querySelectorAll('[data-bundle-count]');
        this.tray = this.querySelector('[data-bundle-tray]');
        this.subtotalEl = this.querySelector('[data-summary-subtotal]');
        this.discountEl = this.querySelector('[data-summary-discount]');
        this.discountRow = this.querySelector('[data-summary-discount-row]');
        this.discountLabelEl = this.querySelector('[data-summary-discount-label]');
        this.totalEl = this.querySelector('[data-summary-total]');
        this.compareEl = this.querySelector('[data-summary-compare]');
        this.pricingStateEl = this.querySelector('[data-summary-pricing-state]');
        this.pricingNoteEl = this.querySelector('[data-summary-pricing-note]');
        this.cta = this.querySelector('[data-bundle-cta]');
        this.ctaLabel = this.querySelector('[data-bundle-cta-label]');
        this.ctaLock = this.querySelector('[data-bundle-cta-lock]');
        this.discountBadgeEl = this.querySelector('[data-bundle-discount-badge]');
        this.paymentsEl = this.querySelector('[data-bundle-payments]');
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.toast = this.querySelector('[data-bundle-toast]');
      }

      readPackPriceCents(attrName, fallbackCents) {
        const raw = this.getAttribute(attrName);
        const parsed = Number.parseInt(String(raw || '').replace(/[^\d]/g, ''), 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackCents;
      }

      readConfig() {
        // Always prefer page section settings (data-buy-*-price). Fallbacks are last resort only.
        this.buy2Price = this.readPackPriceCents('data-buy-2-price', 79900);
        this.buy3Price = this.readPackPriceCents('data-buy-3-price', 104900);
        this.buy4Price = this.readPackPriceCents('data-buy-4-price', 134900);
        this.packPrices = {
          2: this.buy2Price,
          3: this.buy3Price,
          4: this.buy4Price,
        };
        const defaultPack = Number.parseInt(this.dataset.defaultPack || '', 10);
        const urlPack = this.readPackFromQuery();
        this.bundleSize = urlPack || ([2, 3, 4].includes(defaultPack) ? defaultPack : 2);
        this.ctaEmpty = this.dataset.ctaEmpty || 'Select Products';
        this.ctaSingle = this.dataset.ctaSingle || 'Add To Cart';
        this.ctaBundle = 'Secure Checkout';
        this.maxReachedMessage =
          this.dataset.maxReachedMessage || 'Maximum quantity reached.';
        this.variantPlaceholder =
          this.dataset.variantPlaceholder || 'Select option';
        this.intro2 = this.dataset.intro2 || 'Two scents. All yours to choose.';
        this.intro3 = this.dataset.intro3 || 'Three scents. All yours to choose.';
        this.intro4 = this.dataset.intro4 || 'Four scents. All yours to choose.';
      }

      readPackFromQuery() {
        try {
          const pack = Number.parseInt(
            new URLSearchParams(window.location.search).get('pack') || '',
            10
          );
          return [2, 3, 4].includes(pack) ? pack : 0;
        } catch (error) {
          return 0;
        }
      }

      get maxQuantity() {
        return this.bundleSize;
      }

      get cartType() {
        return (
          document.getElementById('PageContainer')?.dataset?.cartType || 'drawer'
        );
      }

      bindEvents() {
        this.cards.forEach((card) => {
          card.addEventListener('click', this.onCardClick);
          card
            .querySelector('[data-bundle-variant]')
            ?.addEventListener('change', this.onVariantChange);
        });
        this.packButtons.forEach((el) => {
          el.addEventListener('click', this.onPackClick);
        });
        this.tray?.addEventListener('click', this.onTrayClick);
        this.cta?.addEventListener('click', this.onCtaClick);
        window.eventBus?.on('drawer:closed', this.onDrawerClosed);
      }

      onPackClick(event) {
        const size = Number.parseInt(event.currentTarget.dataset.bundlePack || '', 10);
        if (![2, 3, 4].includes(size)) return;
        this.setBundleSize(size);
      }

      setBundleSize(size, { silent = false } = {}) {
        const next = [2, 3, 4].includes(size) ? size : 2;
        this.bundleSize = next;

        if (this.totalQuantity() > next) {
          this.trimSelectionsTo(next);
          if (!silent) this.showToast(this.maxReachedMessage);
        }

        this.packButtons.forEach((button) => {
          const isSelected =
            Number.parseInt(button.dataset.bundlePack || '', 10) === next;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        if (this.introEl) {
          const intros = { 2: this.intro2, 3: this.intro3, 4: this.intro4 };
          this.introEl.textContent = intros[next] || this.intro2;
        }
        if (this.maxLabelEl) this.maxLabelEl.textContent = `/${next}`;

        this.steps.forEach((step) => {
          const stepNum = Number.parseInt(step.dataset.step || '', 10);
          step.hidden = stepNum > next;
        });

        if (!silent) {
          this.appliedCartTotals = null;
          this.clearMessages();
          this.render();
        }
      }

      trimSelectionsTo(limit) {
        let total = this.totalQuantity();
        if (total <= limit) return;
        const entries = Array.from(this.selections.entries()).reverse();
        for (const [variantId, item] of entries) {
          while (item.qty > 0 && total > limit) {
            item.qty -= 1;
            total -= 1;
          }
          if (item.qty <= 0) this.selections.delete(variantId);
          if (total <= limit) break;
        }
        this.syncCardQuantities();
      }

      onCardClick(event) {
        if (event.target.closest('[data-bundle-variant]')) return;

        const minus = event.target.closest('[data-bundle-qty-minus]');
        const plus = event.target.closest('[data-bundle-qty-plus]');
        const add = event.target.closest('[data-bundle-add]');
        const media = event.target.closest('.bundle-builder-card__media');
        const countBadge = event.target.closest('[data-bundle-card-count]');
        // Image / badge / add / plus all increment; only minus decrements.
        const shouldIncrement = Boolean(add || plus || media || countBadge);
        if (!minus && !shouldIncrement) return;

        event.preventDefault();
        const card = event.target.closest('[data-bundle-card]');
        if (!card || card.dataset.available === 'false') return;
        const variant = this.getActiveVariant(card);

        if (!variant) {
          this.showToast(this.variantPlaceholder);
          return;
        }
        this.adjustQuantity(card, variant, minus ? -1 : 1);
      }

      onVariantChange(event) {
        const card = event.target.closest('[data-bundle-card]');
        if (!card) return;
        this.appliedCartTotals = null;
        this.clearCardSelections(card);
        const enabled =
          Boolean(event.target.value) &&
          event.target.selectedOptions[0]?.dataset.available !== 'false';
        const price = Number.parseInt(
          event.target.selectedOptions[0]?.dataset.price || '',
          10
        );
        const input = card.querySelector('[data-bundle-qty]');
        if (input) {
          input.value = '0';
          input.disabled = !enabled;
        }
        card.querySelector('[data-bundle-qty-minus]').disabled = true;
        card.querySelector('[data-bundle-qty-plus]').disabled = !enabled;
        card.querySelector('[data-bundle-add]').disabled = !enabled;
        if (price) {
          card.querySelector('[data-card-price]').innerHTML =
            this.formatMoney(price);
        }
        this.render();
      }

      onTrayClick(event) {
        const remove = event.target.closest('[data-remove-variant]');
        if (!remove) return;
        const variantId = remove.dataset.removeVariant;
        const item = this.selections.get(variantId);
        if (!item) return;
        this.appliedCartTotals = null;
        item.qty -= 1;
        if (item.qty <= 0) this.selections.delete(variantId);
        this.syncCardQuantities();
        this.clearMessages();
        this.render();
      }

      getActiveVariant(card) {
        const select = card.querySelector('[data-bundle-variant]');
        const productTitle =
          card.querySelector('.bundle-builder-card__title')?.textContent.trim() ||
          '';
        const image = card.dataset.image || '';
        if (select) {
          if (!select.value) return null;
          const option = select.selectedOptions[0];
          if (option?.dataset.available === 'false') return null;
          return {
            id: String(select.value),
            price: Number.parseInt(option.dataset.price || '', 10) || 0,
            title:
              option.textContent.trim() === 'Default Title'
                ? productTitle
                : `${productTitle} — ${option.textContent.trim()}`,
            image,
          };
        }
        const hidden = card.querySelector('[data-bundle-variant-id]');
        if (!hidden) return null;
        return {
          id: String(hidden.value),
          price: Number.parseInt(hidden.dataset.price || '', 10) || 0,
          title: productTitle,
          image,
        };
      }

      adjustQuantity(card, variant, delta) {
        this.appliedCartTotals = null;
        const total = this.totalQuantity();
        if (delta > 0 && total >= this.maxQuantity) {
          this.showToast(this.maxReachedMessage);
          return;
        }

        const current = this.selections.get(variant.id)?.qty || 0;
        const next = Math.max(0, current + delta);
        if (next === 0) {
          this.selections.delete(variant.id);
        } else {
          this.selections.set(variant.id, { ...variant, qty: next });
        }
        this.syncCardQuantities();
        this.clearMessages();
        this.render();
      }

      clearCardSelections(card) {
        const select = card.querySelector('[data-bundle-variant]');
        if (select) {
          Array.from(select.options).forEach((option) => {
            if (option.value) this.selections.delete(String(option.value));
          });
        }
      }

      cardVariantIds(card) {
        const select = card.querySelector('[data-bundle-variant]');
        if (select) {
          return Array.from(select.options)
            .map((option) => String(option.value))
            .filter(Boolean);
        }
        const id = card.querySelector('[data-bundle-variant-id]')?.value;
        return id ? [String(id)] : [];
      }

      cardQuantity(card) {
        return this.cardVariantIds(card).reduce(
          (sum, id) => sum + (this.selections.get(id)?.qty || 0),
          0
        );
      }

      /**
       * Badge on the product image:
       * - Same product added multiple times → show qty (2, 3, …)
       * - Different products → show selection order (1st pick = 1, 2nd = 2, …)
       */
      cardBadgeNumber(card) {
        const qty = this.cardQuantity(card);
        if (qty <= 0) return 0;
        if (qty > 1) return qty;

        const ids = new Set(this.cardVariantIds(card));
        let order = 0;
        for (const [variantId, item] of this.selections) {
          for (let unit = 0; unit < item.qty; unit += 1) {
            order += 1;
            if (ids.has(String(variantId))) return order;
          }
        }
        return qty;
      }

      syncCardQuantities() {
        this.cards.forEach((card) => {
          const input = card.querySelector('[data-bundle-qty]');
          if (input) input.value = String(this.cardQuantity(card));
        });
      }

      totalQuantity() {
        return Array.from(this.selections.values()).reduce(
          (sum, item) => sum + item.qty,
          0
        );
      }

      subtotal() {
        return Array.from(this.selections.values()).reduce(
          (sum, item) => sum + item.price * item.qty,
          0
        );
      }

      totals() {
        const quantity = this.totalQuantity();
        const subtotal = this.subtotal();
        // Preview total from admin pack prices when qty matches an offer.
        // Subtotal uses real line prices — mixed collections are merchant-owned.
        const advertised = this.packPrices?.[quantity];
        const total = advertised != null ? advertised : subtotal;
        const discount =
          advertised != null ? Math.max(0, subtotal - total) : 0;
        return { quantity, subtotal, total, discount };
      }

      render() {
        const preview = this.totals();
        const { quantity } = preview;
        const { subtotal, total, discount } = this.appliedCartTotals || preview;
        // Show discount UI only when Shopify has applied it, or when the
        // selection matches an admin Buy 2 / 3 / 4 offer.
        const discountApplied = Boolean(
          this.appliedCartTotals && this.appliedCartTotals.discount > 0
        );
        const discountApplicable = !discountApplied && quantity >= 2 && discount > 0;
        const showDiscount = discountApplied || discountApplicable;

        this.counts.forEach((el) => {
          el.textContent = String(quantity);
        });
        if (this.subtotalEl) this.subtotalEl.innerHTML = this.formatMoney(subtotal);
        if (this.discountEl) {
          this.discountEl.innerHTML = showDiscount
            ? `−${this.formatMoney(discount)}`
            : this.formatMoney(0);
        }
        if (this.totalEl) this.totalEl.innerHTML = this.formatMoney(total);
        if (this.compareEl) {
          this.compareEl.innerHTML = this.formatMoney(subtotal);
          this.compareEl.hidden = !showDiscount;
        }
        if (this.discountBadgeEl) {
          const percentage =
            showDiscount && subtotal > 0
              ? Math.round((discount / subtotal) * 100)
              : 0;
          this.discountBadgeEl.innerHTML =
            percentage > 0
              ? `<span>${percentage}%</span><small>OFF</small>`
              : '';
          this.discountBadgeEl.hidden = percentage <= 0;
        }
        this.discountRow?.classList.toggle('is-zero', !showDiscount);
        if (this.discountLabelEl) {
          this.discountLabelEl.textContent = discountApplied
            ? 'Discount'
            : 'Savings';
        }
        if (this.pricingStateEl) {
          this.pricingStateEl.textContent = discountApplied
            ? 'Shopify applied'
            : discountApplicable
              ? 'Estimated'
              : '';
          this.pricingStateEl.hidden = !showDiscount;
          this.pricingStateEl.classList.toggle('is-applied', discountApplied);
        }
        if (this.pricingNoteEl) {
          this.pricingNoteEl.textContent = discountApplied
            ? 'Automatic discount confirmed in your cart'
            : discountApplicable
              ? 'Estimated price — Shopify confirms the discount in cart'
              : 'Select 2, 3 or 4 products to unlock bundle savings';
        }
        this.renderSteps(quantity);
        this.renderTray();
        this.updateButtons(quantity);
      }

      renderSteps(quantity) {
        // Pack of 2 → 1 — 2; Pack of 3 → 1 — 2 — 3
        this.steps.forEach((step) => {
          const stepNum = Number.parseInt(step.dataset.step || '', 10);
          if (stepNum > this.bundleSize) {
            step.hidden = true;
            return;
          }
          step.hidden = false;
          step.classList.toggle('is-filled', quantity >= stepNum);
          step.classList.toggle(
            'is-current',
            quantity + 1 === stepNum && quantity < this.bundleSize
          );
        });
      }

      renderTray() {
        if (!this.tray) return;
        this.tray.replaceChildren();
        let index = 0;
        this.selections.forEach((item, variantId) => {
          for (let unit = 0; unit < item.qty; unit += 1) {
            const tile = document.createElement('div');
            tile.className = 'bundle-builder__tray-item';
            tile.dataset.trayItem = '';
            tile.innerHTML = `
              <div class="bundle-builder__tray-image">
                ${item.image ? `<img src="${this.escapeAttribute(item.image)}" alt="">` : ''}
              </div>
              <button type="button" class="bundle-builder__tray-remove"
                data-remove-variant="${this.escapeAttribute(variantId)}"
                aria-label="Remove ${this.escapeAttribute(item.title)}">
                <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                  <path d="M2.2 2.2l7.6 7.6M9.8 2.2L2.2 9.8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </button>
              <span class="visually-hidden">${this.escapeHtml(item.title)}</span>
            `;
            this.tray.appendChild(tile);
            index += 1;
          }
        });
        while (index < this.maxQuantity) {
          const slot = document.createElement('span');
          slot.className =
            'bundle-builder__tray-item bundle-builder__tray-item--empty';
          slot.innerHTML = `
            <span class="bundle-builder__tray-placeholder-plus" aria-hidden="true">+</span>
            <span class="visually-hidden">Empty bundle slot ${index + 1}</span>
          `;
          this.tray.appendChild(slot);
          index += 1;
        }
      }

      updateButtons(quantity) {
        const atMax = quantity >= this.maxQuantity;
        this.cards.forEach((card) => {
          if (card.dataset.available === 'false') return;
          const hasVariant = Boolean(this.getActiveVariant(card));
          const cardQty = this.cardQuantity(card);
          const add = card.querySelector('[data-bundle-add]');
          const quantityWrap = card.querySelector('[data-bundle-qty-wrap]');
          const countBadge = card.querySelector('[data-bundle-card-count]');
          if (add) {
            add.hidden = cardQty > 0;
            add.disabled = !hasVariant || atMax;
          }
          if (quantityWrap) quantityWrap.hidden = cardQty <= 0;
          if (countBadge) {
            const badgeNumber = this.cardBadgeNumber(card);
            countBadge.textContent = String(badgeNumber);
            countBadge.hidden = badgeNumber <= 0;
          }
          card.classList.toggle('is-selected', cardQty > 0);
          card.querySelector('[data-bundle-qty-plus]').disabled =
            !hasVariant || atMax;
          card.querySelector('[data-bundle-qty-minus]').disabled =
            !hasVariant || cardQty <= 0;
        });

        if (!this.cta || !this.ctaLabel) return;
        // Minimum 2 products required — single-item checkout is disallowed.
        this.cta.disabled = quantity < 2 || this.isSubmitting;
        if (quantity === 0) this.ctaLabel.textContent = this.ctaEmpty;
        else this.ctaLabel.textContent = this.ctaBundle;
        if (this.ctaLock) this.ctaLock.hidden = quantity < 2;
        if (this.paymentsEl) this.paymentsEl.hidden = quantity < 2;
      }

      async onCtaClick(event) {
        event.preventDefault();
        const { quantity } = this.totals();
        if (quantity < 2 || this.isSubmitting) return;

        const items = Array.from(this.selections.entries()).map(([id, item]) => ({
          id: Number(id),
          quantity: item.qty,
        }));

        this.isSubmitting = true;
        this.clearMessages();
        this.setLoading(true);
        this.render();

        try {
          // Fresh cart: never mix bundle with existing cart items
          await this.clearCart();
          await this.addItemsToCart(items);

          const cart = await this.fetchCart();
          this.applyCartTotals(cart);
          window.wetheme?.updateCartCount?.(cart);
          if (this.cartType === 'drawer') {
            document.body.classList.add('bundle-drawer-open');
            window.eventBus?.emit('render:cart:drawer');
            window.eventBus?.emit('open:cart:drawer', { scrollToTop: true });
            if (items.reduce((sum, item) => sum + item.quantity, 0) >= 2) {
              this.celebrate();
            }
            // Let the celebration play before GoKwik covers the storefront.
            this.checkoutTimer = setTimeout(
              () => this.startGokwikCheckout(),
              900
            );
          }
          // Selections survive on purpose: cancelling payment should drop the
          // shopper back onto an intact bundle, not an empty one.
        } catch (error) {
          this.showError(error.message || 'Unable to add products.');
          if (this.cartType === 'drawer') {
            window.eventBus?.emit('render:cart:drawer');
          }
        } finally {
          this.isSubmitting = false;
          this.setLoading(false);
          this.render();
        }
      }

      async clearCart() {
        const response = await fetch(window.routes.cart_clear_url || '/cart/clear.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error('Unable to clear cart.');
        }
        return response.json().catch(() => ({}));
      }

      async addItemsToCart(items) {
        const payload = { items };
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(
            data?.description || data?.message || 'Unable to add products.'
          );
        }
        return data;
      }

      async fetchCart() {
        try {
          return await (
            await fetch(`${window.routes.cart_url}?view=compare`)
          ).json();
        } catch (_error) {
          return await (await fetch('/cart.js')).json();
        }
      }

      applyCartTotals(cart) {
        const subtotal = Number(cart?.original_total_price);
        const total = Number(cart?.total_price);
        if (!Number.isFinite(subtotal) || !Number.isFinite(total)) return;
        const reportedDiscount = Number(cart?.total_discount) || 0;
        const discount = Math.max(reportedDiscount, subtotal - total, 0);
        // Shopify only reports the automatic discount once it has actually been
        // applied. Until then keep showing the estimate rather than dropping the
        // savings back to zero.
        if (discount <= 0) {
          this.appliedCartTotals = null;
          this.render();
          return;
        }
        this.appliedCartTotals = {
          quantity: Number(cart?.item_count) || this.totalQuantity(),
          subtotal,
          total,
          discount,
        };
        this.render();
      }

      // The drawer re-renders on every cart change and GoKwik only enables its
      // button once its own script has initialised, so poll instead of assuming.
      async startGokwikCheckout() {
        const button = await this.waitForGokwikButton();
        if (!button) return;
        this.watchGokwikDismiss();
        button.click();
      }

      waitForGokwikButton(timeout = 8000) {
        const deadline = Date.now() + timeout;
        return new Promise((resolve) => {
          const poll = () => {
            const button = document.querySelector('.gokwik-checkout button');
            if (button && !button.disabled) {
              resolve(button);
            } else if (Date.now() > deadline) {
              resolve(null);
            } else {
              setTimeout(poll, 200);
            }
          };
          poll();
        });
      }

      // GoKwik exposes no "popup closed" event, so treat the removal of the
      // node it appends to <body> as the shopper cancelling payment.
      watchGokwikDismiss() {
        const known = new Set(document.body.children);
        const ignored = ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'];
        let popup = null;

        this.gokwikObserver?.disconnect();
        this.gokwikObserver = new MutationObserver(() => {
          if (!popup) {
            popup = Array.from(document.body.children).find(
              (el) => !known.has(el) && !ignored.includes(el.tagName)
            );
            return;
          }
          if (!popup.isConnected) {
            this.gokwikObserver.disconnect();
            this.closeCartDrawer();
          }
        });
        this.gokwikObserver.observe(document.body, { childList: true });
      }

      closeCartDrawer() {
        document.body.classList.remove('bundle-drawer-open');
        window.eventBus?.emit('close:all:drawers');
      }

      celebrate() {
        if (this.celebrateTimer) clearTimeout(this.celebrateTimer);
        document.querySelector('[data-bundle-celebration-root]')?.remove();
        const celebration = document.createElement('div');
        celebration.className = 'bundle-builder__celebrate is-active';
        celebration.dataset.bundleCelebrationRoot = '';
        celebration.setAttribute('aria-hidden', 'true');
        document.body.appendChild(celebration);

        const themeStyles = getComputedStyle(this);
        [
          '--primary-btn-bg-color',
          '--primary-btn-text-color',
          '--text-color',
          '--body-color',
        ].forEach((property) => {
          celebration.style.setProperty(
            property,
            themeStyles.getPropertyValue(property)
          );
        });
        const colors = [
          themeStyles.getPropertyValue('--primary-btn-bg-color').trim(),
          themeStyles.getPropertyValue('--primary-btn-text-color').trim(),
          themeStyles.getPropertyValue('--text-color').trim(),
          themeStyles.getPropertyValue('--body-color').trim(),
        ].filter(Boolean);
        const pieceCount = 54;
        for (let i = 0; i < pieceCount; i += 1) {
          const piece = document.createElement('span');
          piece.className = 'bundle-builder__confetti';
          const angle = (360 / pieceCount) * i + (Math.random() * 18 - 9);
          const distance = 130 + Math.random() * 240;
          const size = 6 + Math.random() * 6;
          piece.style.setProperty('--angle', `${angle}deg`);
          piece.style.setProperty('--distance', `${distance}px`);
          piece.style.setProperty('--size', `${size}px`);
          piece.style.setProperty('--delay', `${Math.random() * 120}ms`);
          piece.style.setProperty('--spin', `${(Math.random() * 540 - 270).toFixed(0)}deg`);
          piece.style.background = colors[i % colors.length] || 'currentColor';
          if (i % 3 === 0) piece.classList.add('is-round');
          celebration.appendChild(piece);
        }

        document.body.classList.add('bundle-celebrate');
        this.celebrateTimer = setTimeout(() => {
          celebration.remove();
          document.body.classList.remove('bundle-celebrate');
        }, 1800);
      }

      setLoading(loading) {
        this.cta?.classList.toggle('is-loading', loading);
        this.cta?.setAttribute('aria-busy', loading ? 'true' : 'false');
      }

      showToast(message) {
        if (!this.toast) return;
        this.toast.textContent = message;
        this.toast.classList.add('is-visible');
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(
          () => this.toast.classList.remove('is-visible'),
          2200
        );
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.add('is-visible');
      }

      clearMessages() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('is-visible');
      }

      formatMoney(cents) {
        return window.wetheme?.formatMoney
          ? window.wetheme.formatMoney(cents)
          : `₹${(Number(cents) / 100).toFixed(0)}`;
      }

      escapeAttribute(value) {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('"', '&quot;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;');
      }

      escapeHtml(value) {
        return this.escapeAttribute(value).replaceAll("'", '&#039;');
      }
    }
  );
}
